import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "reactstrap";

import { MEDIA_SOUP_UNSUPPORTED_CODEC_EVENT, formatCodecInfo } from "../../lib/MediaSoupClient";

/**
 * 영상 수신 코덱 미지원 및 송출 실패를 중앙 안내 모달로 표시한다.
 *
 * @since 2026-08-07.
 */
const MediaSoupUnsupportedCodecModal = ({ direction = "consume" }) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [failureInfo, setFailureInfo] = useState(null);
    const displayedFailuresRef = useRef(new Set());

    useEffect(() => {
        const handleUnsupportedCodec = event => {
            const detail = event && event.detail ? event.detail : {};
            // 20261022 스마트TV는 기존 수신 전용, live 페이지는 송수신 안내를 함께 사용한다.
            if (direction !== "all" && (detail.direction || "consume") !== direction) return;
            const codecs = Array.isArray(detail.producerCodecs) ? detail.producerCodecs : [];
            const failureKey = `${detail.direction || "consume"}:${detail.prodUserSeq}:${detail.mediaKind}:${detail.errorCode}:${codecs.join(",")}`;
            if (displayedFailuresRef.current.has(failureKey)) return;

            // 20260807 동일 producer의 반복 실패가 모달을 계속 띄우지 않도록 접속 세션 동안 한 번만 표시한다.
            displayedFailuresRef.current.add(failureKey);
            setFailureInfo(detail);
            setIsOpen(true);
        };

        window.addEventListener(MEDIA_SOUP_UNSUPPORTED_CODEC_EVENT, handleUnsupportedCodec);
        return () => window.removeEventListener(MEDIA_SOUP_UNSUPPORTED_CODEC_EVENT, handleUnsupportedCodec);
    }, [direction]);

    const isProduceFailure = failureInfo && failureInfo.direction === "produce";
    const getMessageKey = () => {
        if (isProduceFailure) {
            return failureInfo && failureInfo.errorCode === "UnsupportedError"
                ? "미디어_코덱_지원_불가_송출_영상" : "미디어_영상_송출_생성_실패";
        }
        // 20260807 공유 수신 화면에서도 실제 producer 종류에 맞는 안내가 보이도록 roomKind로 구분한다.
        return failureInfo && failureInfo.roomKind === "handsup"
            ? "미디어_코덱_지원_불가_학생_영상"
            : "미디어_코덱_지원_불가_선생님_영상";
    };

    // 20260904 서버가 실패 응답에 실어 보내는 producerCodecs(예: ["video/vp9"])에서 첫 값을 사람이 읽을 코덱명으로 변환한다. ... by claude
    const producerCodecs = failureInfo && Array.isArray(failureInfo.producerCodecs) ? failureInfo.producerCodecs : [];
    const codecInfo = producerCodecs.length > 0 ? formatCodecInfo(producerCodecs[0]) : null;
    // 20261022 자동 협상 실패 시 후보 목록을 실제 사용 코덱으로 단정하지 않는다.
    const codecNames = producerCodecs.map(formatCodecInfo).filter(Boolean).map(info => info.codecName).join(", ");

    // 20260904 부트스트랩 card/badge 기본 스타일 대신, 라이트/다크(soft-dark) 테마가
    // 이미 대응되어 있는 hc-session-alert-* 클래스 체계로 교체한다(LogoutAlertModal.js와 동일 패턴). by claude
    return (
        <Modal isOpen={isOpen} centered backdrop="static" keyboard={false} className="hc-session-alert-modal" contentClassName="hc-session-alert-content">
            <div className="hc-session-alert-card">
                <div className="hc-session-alert-body">
                    <div className="hc-session-alert-title">
                        <span>{t(isProduceFailure ? "미디어_영상_송출_실패_제목" : "미디어_코덱_지원_불가_제목", { ns: "live" })}</span>
                    </div>
                    {
                        codecInfo &&
                        <span className="badge badge-danger px-3 py-2" style={{ fontSize: '0.85rem' }}>
                            {t(isProduceFailure ? "미디어_송출_코덱_후보" : "코덱", { ns: "live" })} {isProduceFailure ? codecNames : codecInfo.codecName}
                        </span>
                    }
                    <div className="hc-session-alert-message">
                        <div className="hc-session-alert-text">
                            <span>{t(getMessageKey(), { ns: "live", codec: codecInfo ? codecInfo.codecName : t("알_수_없음", { ns: "live" }) })}</span>
                            <span>[Code.MEDIA001]</span>
                        </div>
                    </div>
                    <button type="button" className="hc-session-alert-btn hc-session-alert-btn-primary" autoFocus onClick={() => setIsOpen(false)}>
                        {t("확인", { ns: "common" })}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default MediaSoupUnsupportedCodecModal;
